import type { StorageConfig, StorageZone, StorageZoneCreated } from '@erp/shared';
import {
  AlertTriangle,
  Database,
  HardDrive,
  KeyRound,
  Plus,
  RefreshCw,
  Server,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '../../components/shadcn/index.js';
import { Button, CopyButton, EmptyState, FormField, confirmDialog } from '../../components/ui/index.js';
import { platformApi } from '../../lib/platformApi.js';

/** The same rule the storage service enforces on a zone name, so a typo is
 *  caught in the form rather than after a round trip. */
const NAME_RE = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

const EMPTY_FORM = { name: '', quotaGb: '50', isPublic: false, label: '' };

function fmtBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const value = n / 1024 ** i;
  return `${value >= 100 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

function errMessage(e: unknown): string | undefined {
  const cause = (e as { response?: { data?: { cause?: { message?: string; code?: string } } } })
    .response?.data?.cause;
  return cause?.message ?? cause?.code;
}

/**
 * Platform-admin console for Vylo Storage — object storage and video for
 * customers, kept on a Hetzner Storage Box and served from cdn.vylo-tech.com.
 *
 * One customer is one zone: its own directory on the box, its own key, its
 * own quota. The key is minted by the storage service, which keeps only its
 * hash, so it is shown here once and cannot be recovered afterwards — the
 * reveal dialog says so and rotation is the answer to a lost one.
 *
 * Everything is proxied through the api; the storage service's admin token
 * never reaches the browser.
 */
export function PlatformStoragePage(): JSX.Element {
  const { t } = useTranslation('platform');
  const { t: tc } = useTranslation();
  const [config, setConfig] = useState<StorageConfig | null>(null);
  const [rows, setRows] = useState<StorageZone[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [created, setCreated] = useState<StorageZoneCreated | null>(null);
  const [rotated, setRotated] = useState<{ name: string; key: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadZones = useCallback(async (): Promise<void> => {
    try {
      const r = await platformApi().get<{ items: StorageZone[] }>('/storage/zones');
      setRows(r.data.items);
    } catch {
      setRows([]);
    }
  }, []);

  const loadConfig = useCallback(async (): Promise<void> => {
    try {
      const r = await platformApi().get<StorageConfig>('/storage/config');
      setConfig(r.data);
    } catch {
      setConfig(null);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
    void loadZones();
  }, [loadConfig, loadZones]);

  const nameValid = NAME_RE.test(form.name) && form.name.length >= 3 && form.name.length <= 32;

  async function create(): Promise<void> {
    if (!nameValid) return;
    setSaving(true);
    try {
      const r = await platformApi().post<StorageZoneCreated>('/storage/zones', {
        name: form.name,
        isPublic: form.isPublic,
        quotaGb: Number(form.quotaGb) || 0,
        ...(form.label.trim() ? { label: form.label.trim() } : {}),
      });
      setCreateOpen(false);
      setForm({ ...EMPTY_FORM });
      setCreated(r.data);
      await loadZones();
    } catch (e) {
      toast.error(errMessage(e) ?? t('storage.failed', { defaultValue: 'That did not work.' }));
    } finally {
      setSaving(false);
    }
  }

  async function setQuota(zone: StorageZone): Promise<void> {
    const raw = window.prompt(
      t('storage.quotaPrompt', { defaultValue: 'Quota in GB for {{name}} (0 = unlimited)', name: zone.name }),
      String(Math.round((zone.quotaBytes / 1024 ** 3) * 10) / 10),
    );
    if (raw === null) return;
    const quotaGb = Number(raw);
    if (!Number.isFinite(quotaGb) || quotaGb < 0) return;
    setBusy(zone.name);
    try {
      await platformApi().patch(`/storage/zones/${zone.name}`, { quotaGb });
      await loadZones();
    } catch (e) {
      toast.error(errMessage(e) ?? t('storage.failed', { defaultValue: 'That did not work.' }));
    } finally {
      setBusy(null);
    }
  }

  async function togglePublic(zone: StorageZone): Promise<void> {
    setBusy(zone.name);
    try {
      await platformApi().patch(`/storage/zones/${zone.name}`, { isPublic: !zone.isPublic });
      await loadZones();
    } catch (e) {
      toast.error(errMessage(e) ?? t('storage.failed', { defaultValue: 'That did not work.' }));
    } finally {
      setBusy(null);
    }
  }

  async function rotate(zone: StorageZone): Promise<void> {
    const ok = await confirmDialog({
      title: t('storage.rotate.title', { defaultValue: 'Rotate the key for {{name}}?', name: zone.name }),
      message: t('storage.rotate.body', {
        defaultValue:
          'The current key stops working immediately, and anything using it stops uploading until it is given the new one.',
      }),
      confirmLabel: t('storage.rotate.confirm', { defaultValue: 'Rotate key' }),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(zone.name);
    try {
      const r = await platformApi().post<{ name: string; key: string }>(
        `/storage/zones/${zone.name}/rotate`,
      );
      setRotated(r.data);
    } catch (e) {
      toast.error(errMessage(e) ?? t('storage.failed', { defaultValue: 'That did not work.' }));
    } finally {
      setBusy(null);
    }
  }

  async function remove(zone: StorageZone): Promise<void> {
    const ok = await confirmDialog({
      title: t('storage.delete.title', { defaultValue: 'Delete zone {{name}}?', name: zone.name }),
      message: t('storage.delete.body', {
        defaultValue:
          'Only an empty zone can be deleted. Its files must be removed first, and nothing here brings them back.',
      }),
      confirmLabel: tc('actions.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(zone.name);
    try {
      await platformApi().delete(`/storage/zones/${zone.name}`);
      await loadZones();
    } catch (e) {
      toast.error(errMessage(e) ?? t('storage.failed', { defaultValue: 'That did not work.' }));
    } finally {
      setBusy(null);
    }
  }

  const revealKey = created?.key ?? rotated?.key ?? null;
  const revealName = created?.name ?? rotated?.name ?? '';
  const cdn = config?.cdnUrl ?? 'https://cdn.vylo-tech.com';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-fg">
            {t('storage.title', { defaultValue: 'Storage' })}
          </h1>
          <p className="text-sm text-fg-muted">
            {t('storage.subtitle', {
              defaultValue: 'One zone per customer — uploads, quota and delivery links.',
            })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => { void loadConfig(); void loadZones(); }}>
            <RefreshCw className="size-4" />
            {t('storage.refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button onClick={() => setCreateOpen(true)} disabled={!config?.configured}>
            <Plus className="size-4" />
            {t('storage.new', { defaultValue: 'New zone' })}
          </Button>
        </div>
      </header>

      <ServiceStatus config={config} />

      {rows === null ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Database className="size-6" />}
          title={t('storage.empty.title', { defaultValue: 'No zones yet' })}
          description={t('storage.empty.body', {
            defaultValue: 'A zone is one customer: a directory on the box, a key, and a quota.',
          })}
        />
      ) : (
        <div className="grid gap-3">
          {rows.map((zone) => (
            <ZoneCard
              key={zone.name}
              zone={zone}
              cdn={cdn}
              busy={busy === zone.name}
              onQuota={() => void setQuota(zone)}
              onTogglePublic={() => void togglePublic(zone)}
              onRotate={() => void rotate(zone)}
              onDelete={() => void remove(zone)}
            />
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('storage.new', { defaultValue: 'New zone' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <FormField
              id="storage-zone-name"
              label={t('storage.form.name', { defaultValue: 'Zone name' })}
              helperText={t('storage.form.nameHint', {
                defaultValue: 'Lowercase letters, digits and hyphens. This appears in every link.',
              })}
              error={
                form.name && !nameValid
                  ? t('storage.form.nameBad', { defaultValue: '3-32 characters, a-z 0-9 and -' })
                  : undefined
              }
            >
              <Input
                id="storage-zone-name"
                value={form.name}
                placeholder="duhok-academy"
                onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}
              />
            </FormField>
            <FormField
              id="storage-zone-label"
              label={t('storage.form.label', { defaultValue: 'Who is it for (optional)' })}
            >
              <Input
                id="storage-zone-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </FormField>
            <FormField
              id="storage-zone-quota"
              label={t('storage.form.quota', { defaultValue: 'Quota (GB)' })}
              helperText={t('storage.form.quotaHint', { defaultValue: '0 means no ceiling.' })}
            >
              <Input
                id="storage-zone-quota"
                type="number"
                min={0}
                value={form.quotaGb}
                onChange={(e) => setForm({ ...form, quotaGb: e.target.value })}
              />
            </FormField>
            <label className="flex items-start gap-2 text-sm text-fg">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.isPublic}
                onChange={(e) => setForm({ ...form, isPublic: e.target.checked })}
              />
              <span>
                {t('storage.form.public', { defaultValue: 'Serve without a signed link' })}
                <span className="block text-xs text-fg-muted">
                  {t('storage.form.publicHint', {
                    defaultValue:
                      'Anyone with the URL can fetch it. Leave off for anything a customer would not publish.',
                  })}
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={() => void create()} disabled={!nameValid || saving}>
              {tc('actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <KeyReveal
        zoneKey={revealKey}
        zoneName={revealName}
        cdn={cdn}
        onDone={() => { setCreated(null); setRotated(null); }}
      />
    </div>
  );
}

/**
 * Three facts, not one: this container's config, the storage service, and the
 * Storage Box behind it. They fail independently and the fix for each is
 * different, and the one that matters most is the last — a reachable service
 * with an unreachable box accepts uploads it cannot finish.
 */
function ServiceStatus({ config }: { config: StorageConfig | null }): JSX.Element {
  const { t } = useTranslation('platform');
  if (!config) return <Skeleton className="h-20 w-full" />;

  if (!config.configured) {
    return (
      <Card className="flex items-start gap-3 border-warning/40 bg-warning/10 p-4">
        <AlertTriangle className="mt-0.5 size-5 text-warning" />
        <div className="text-sm">
          <p className="font-medium text-fg">
            {t('storage.status.unconfigured', { defaultValue: 'Storage is not configured' })}
          </p>
          <p className="text-fg-muted">
            {t('storage.status.unconfiguredBody', {
              defaultValue:
                'Set VYLO_STORAGE_URL and VYLO_STORAGE_ADMIN_TOKEN in the api environment.',
            })}
          </p>
        </div>
      </Card>
    );
  }

  const disk = config.disk;
  const cache = config.cache;
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Card className="flex items-center gap-3 p-4">
        <Server className="size-5 text-fg-muted" />
        <div className="min-w-0">
          <p className="text-xs text-fg-muted">
            {t('storage.status.service', { defaultValue: 'Service' })}
          </p>
          <Badge variant={config.reachable ? 'success' : 'secondary'}>
            {config.reachable
              ? t('storage.status.up', { defaultValue: 'reachable' })
              : t('storage.status.down', { defaultValue: 'unreachable' })}
          </Badge>
        </div>
      </Card>
      <Card className="flex items-center gap-3 p-4">
        <Database className="size-5 text-fg-muted" />
        <div className="min-w-0">
          <p className="text-xs text-fg-muted">
            {t('storage.status.box', { defaultValue: 'Storage Box' })}
          </p>
          <Badge variant={config.boxReachable ? 'success' : 'danger'}>
            {config.boxReachable
              ? t('storage.status.up', { defaultValue: 'reachable' })
              : t('storage.status.down', { defaultValue: 'unreachable' })}
          </Badge>
          {config.queue && config.queue.failed > 0 ? (
            <p className="truncate text-xs text-danger">
              {t('storage.status.failed', {
                defaultValue: '{{count}} upload(s) gave up',
                count: config.queue.failed,
              })}
            </p>
          ) : null}
        </div>
      </Card>
      <Card className="flex items-center gap-3 p-4">
        <HardDrive className="size-5 text-fg-muted" />
        <div className="min-w-0">
          <p className="text-xs text-fg-muted">
            {t('storage.status.disk', { defaultValue: 'Delivery disk' })}
          </p>
          <p className="text-sm text-fg">
            {disk ? fmtBytes(disk.freeBytes) : '—'}
            {cache ? ` · ${t('storage.status.cache', { defaultValue: 'cache' })} ${fmtBytes(cache.bytes)}` : ''}
          </p>
          {disk && !disk.acceptingUploads ? (
            <p className="text-xs text-danger">
              {t('storage.status.refusing', { defaultValue: 'refusing uploads — disk floor' })}
            </p>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function ZoneCard({
  zone, cdn, busy, onQuota, onTogglePublic, onRotate, onDelete,
}: {
  zone: StorageZone;
  cdn: string;
  busy: boolean;
  onQuota: () => void;
  onTogglePublic: () => void;
  onRotate: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation('platform');
  const { t: tc } = useTranslation();
  const pct = zone.quotaUsedPct;
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-fg">{zone.name}</span>
            <Badge variant={zone.isPublic ? 'warning' : 'secondary'}>
              {zone.isPublic
                ? t('storage.zone.public', { defaultValue: 'public' })
                : t('storage.zone.signed', { defaultValue: 'signed links' })}
            </Badge>
            {zone.pending > 0 ? (
              <Badge variant="warning">
                {t('storage.zone.pending', {
                  defaultValue: '{{count}} on the way to the box',
                  count: zone.pending,
                })}
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-fg-muted">
            {t('storage.zone.usage', {
              defaultValue: '{{used}} in {{objects}} file(s)',
              used: fmtBytes(zone.usedBytes),
              objects: zone.objects,
            })}
            {zone.quotaBytes > 0
              ? ` · ${t('storage.zone.of', { defaultValue: 'of' })} ${fmtBytes(zone.quotaBytes)}${
                  pct !== null ? ` (${pct}%)` : ''
                }`
              : ` · ${t('storage.zone.noQuota', { defaultValue: 'no quota' })}`}
          </p>
          <div className="flex items-center gap-2">
            <code className="truncate rounded bg-bg-subtle px-1.5 py-0.5 text-xs text-fg-muted">
              {`${cdn}/${zone.name}/`}
            </code>
            <CopyButton value={`${cdn}/${zone.name}/`} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={onQuota} disabled={busy}>
            {t('storage.actions.quota', { defaultValue: 'Quota' })}
          </Button>
          <Button variant="ghost" size="sm" onClick={onTogglePublic} disabled={busy}>
            {zone.isPublic
              ? t('storage.actions.makeSigned', { defaultValue: 'Require signing' })
              : t('storage.actions.makePublic', { defaultValue: 'Make public' })}
          </Button>
          <Button variant="ghost" size="sm" onClick={onRotate} disabled={busy}>
            <KeyRound className="size-4" />
            {t('storage.actions.rotate', { defaultValue: 'Rotate key' })}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} disabled={busy}>
            <Trash2 className="size-4 text-danger" />
            <span className="sr-only">{tc('actions.delete')}</span>
          </Button>
        </div>
      </div>
      {zone.quotaBytes > 0 ? (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle">
          <div
            className={`h-full rounded-full ${(pct ?? 0) >= 90 ? 'bg-danger' : 'bg-primary'}`}
            style={{ width: `${Math.min(100, pct ?? 0)}%` }}
          />
        </div>
      ) : null}
    </Card>
  );
}

/**
 * One-time key reveal after create or rotate. The storage service keeps only
 * the hash, so this is the only time the key exists anywhere a person can
 * read it — the dialog ignores outside-click and Escape, and the only way out
 * is the explicit button.
 */
function KeyReveal({
  zoneKey, zoneName, cdn, onDone,
}: {
  zoneKey: string | null;
  zoneName: string;
  cdn: string;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation('platform');

  // Pasted into a developer channel, like the OTP docs snippets — deliberately
  // untranslated.
  const snippet = [
    `Upload:  https://storage.vylo-tech.com/${zoneName}/<path>`,
    `Deliver: ${cdn}/${zoneName}/<path>`,
    `Key:     ${zoneKey ?? ''}`,
    '',
    `curl -X PUT --data-binary @lecture.mp4 \\`,
    `  -H "Authorization: Bearer ${zoneKey ?? ''}" \\`,
    `  -H "Content-Type: video/mp4" \\`,
    `  https://storage.vylo-tech.com/${zoneName}/w1/intro.mp4`,
  ].join('\n');

  return (
    <Dialog open={zoneKey !== null} onOpenChange={() => undefined}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('storage.reveal.title', { defaultValue: 'Key for {{name}}', name: zoneName })}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-fg">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
          <p>
            {t('storage.reveal.warning', {
              defaultValue:
                'Copy this now. Only its hash is stored, so it cannot be shown again — a lost key is replaced by rotating, not by looking it up.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded bg-bg-subtle px-2 py-1.5 text-sm">
            {zoneKey}
          </code>
          <CopyButton value={zoneKey ?? ''} />
        </div>
        <pre className="max-h-48 overflow-auto rounded bg-bg-subtle p-3 text-xs text-fg-muted">
          {snippet}
        </pre>
        <DialogFooter>
          <CopyButton value={snippet} />
          <Button onClick={onDone}>{t('storage.reveal.done', { defaultValue: 'Done' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
